import { authorityStateForQuestion } from "./authorityProgress";
import {
  TREATMENT_RUNTIME_STATE,
  TREATMENT_RUNTIME_STATE_ORDER,
  TREATMENT_RUNTIME_TERMINAL_STATES,
} from "./treatmentApiConstants";

/** Keep authored order within each phase while following the runtime phase order. */
export const orderQuestionsByRuntimeBoundary = (questions) => (
  [...(Array.isArray(questions) ? questions : [])].sort((left, right) => (
    (TREATMENT_RUNTIME_STATE_ORDER[authorityStateForQuestion(left)] ?? Number.MAX_SAFE_INTEGER)
    - (TREATMENT_RUNTIME_STATE_ORDER[authorityStateForQuestion(right)] ?? Number.MAX_SAFE_INTEGER)
  ))
);

export const isTerminalRuntimeState = (state) => (
  state === TREATMENT_RUNTIME_STATE.disqualified
  || TREATMENT_RUNTIME_TERMINAL_STATES.includes(state)
);

/**
 * Separates UI step movement from server-state movement.
 *
 * Several visible questions can belong to one backend runtime state. Moving
 * between them must be local: asking the backend to leave `treatment_screening`
 * after the first of three questions makes it validate all three and reject the
 * transition, which is what produced the raw `required_answers: <uuid>` error
 * backend required-answer validation before the boundary is complete.
 */

export const boundaryStateForStep = (step) => {
  if (!step) return null;
  // A grouped step (e.g. several product questions) shares one authority state;
  // the first question determines it.
  return authorityStateForQuestion(step.question || step.questions?.[0] || null);
};

/** Authored placements from later runtime phases cannot be Back targets. */
export const previousRuntimeJourneyStep = (steps, currentStepId) => {
  const visible = Array.isArray(steps) ? steps : [];
  const currentIndex = visible.findIndex((step) => step.id === currentStepId);
  if (currentIndex <= 0) return null;
  const currentOrder = TREATMENT_RUNTIME_STATE_ORDER[
    boundaryStateForStep(visible[currentIndex])
  ];
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const candidateOrder = TREATMENT_RUNTIME_STATE_ORDER[
      boundaryStateForStep(visible[index])
    ];
    if (
      currentOrder === undefined
      || (candidateOrder !== undefined && candidateOrder <= currentOrder)
    ) return visible[index];
  }
  return null;
};

/**
 * A released Custom Program can carry a Program-owned copy of the shared
 * Service Area Check later in its static question order. Once the runtime has
 * advanced beyond routing, that copy is already satisfied by the visit-level
 * routing transition and must not be rendered again.
 */
export const runtimeHasPassedRoutingBoundary = (runtimeState) => {
  const routingOrder = TREATMENT_RUNTIME_STATE_ORDER[TREATMENT_RUNTIME_STATE.routing];
  const currentOrder = TREATMENT_RUNTIME_STATE_ORDER[runtimeState];
  return currentOrder !== undefined && currentOrder > routingOrder;
};

/**
 * Whether a static questionnaire node belongs to an authority boundary the
 * server has already completed. The server state is the cursor authority;
 * stale routing/common nodes must not remain renderable after it advances.
 */
export const questionIsBeforeRuntimeBoundary = (question, runtimeState) => {
  const currentOrder = TREATMENT_RUNTIME_STATE_ORDER[runtimeState];
  const questionState = authorityStateForQuestion(question);
  const questionOrder = TREATMENT_RUNTIME_STATE_ORDER[questionState];
  if (currentOrder === undefined || questionOrder === undefined) return false;
  return questionOrder < currentOrder;
};

/**
 * Does moving from `currentStep` to `nextStep` cross a backend runtime state?
 *
 * `false` means save a local draft and navigate. `true` means validate the
 * whole boundary and synchronize before navigating.
 */
export const crossesRuntimeBoundary = (currentStep, nextStep) => {
  if (!currentStep || !nextStep) return true;
  const from = boundaryStateForStep(currentStep);
  const to = boundaryStateForStep(nextStep);
  if (!from || !to) return true;
  return from !== to;
};

/** Every visible step that belongs to the same runtime state as `step`. */
export const stepsInBoundary = (steps, step) => {
  const target = boundaryStateForStep(step);
  if (!target) return [];
  return (Array.isArray(steps) ? steps : []).filter(
    (candidate) => boundaryStateForStep(candidate) === target
  );
};

/**
 * Plan the move from the active step to the next one.
 *
 * Returns `{ mode, boundarySteps }` where mode is `"local"` (persist a draft and
 * navigate) or `"synchronize"` (validate the complete boundary payload, call
 * the runtime, then navigate on success).
 */
export const planStepTransition = (steps, currentStepId, nextStepId) => {
  const visible = Array.isArray(steps) ? steps : [];
  const current = visible.find((step) => step.id === currentStepId) || null;
  const next = visible.find((step) => step.id === nextStepId) || null;

  if (!crossesRuntimeBoundary(current, next)) {
    return Object.freeze({ mode: "local", boundarySteps: Object.freeze([]) });
  }
  return Object.freeze({
    mode: "synchronize",
    boundarySteps: Object.freeze(stepsInBoundary(visible, current)),
  });
};

/**
 * Find the preceding runtime boundary from the rendered journey, rather than
 * the authored manifest. The two differ when a conditional boundary (such as
 * program labs) has no visible content. Back must not spend one press moving
 * through an invisible boundary.
 */
export const previousVisibleAuthorityState = ({
  visibleQuestions,
  currentQuestion,
  stateForQuestion = authorityStateForQuestion,
}) => {
  const visible = Array.isArray(visibleQuestions) ? visibleQuestions : [];
  const identities = [
    currentQuestion?.id,
    currentQuestion?.source_id,
    currentQuestion?.release_node_id,
  ].filter(Boolean).map(String);
  const currentIndex = visible.findIndex((question) => (
    [question?.id, question?.source_id, question?.release_node_id]
      .filter(Boolean)
      .map(String)
      .some((identity) => identities.includes(identity))
  ));
  if (currentIndex <= 0) return null;
  const currentState = stateForQuestion(visible[currentIndex]);
  const currentOrder = TREATMENT_RUNTIME_STATE_ORDER[currentState];
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    const state = stateForQuestion(visible[index]);
    if (
      state && state !== currentState
      && (
        currentOrder === undefined
        || TREATMENT_RUNTIME_STATE_ORDER[state] < currentOrder
      )
    ) return state;
  }
  return null;
};

export const previousJourneyAuthorityState = ({
  previousTreatmentStep,
  previousVisibleState,
  fallbackState,
  currentState = null,
}) => {
  const currentOrder = TREATMENT_RUNTIME_STATE_ORDER[currentState];
  return [
    authorityStateForQuestion(previousTreatmentStep?.question),
    previousVisibleState,
    fallbackState,
  ].find((state) => (
    state
    && (currentOrder === undefined || TREATMENT_RUNTIME_STATE_ORDER[state] <= currentOrder)
  )) || currentState || null;
};

/**
 * Map a `required_answers_missing` response to the first visible step the
 * patient should be taken back to.
 *
 * The backend sends `[{ source_id, label }]`. Source IDs are for mapping and
 * diagnostics only — never render one.
 */
export const resolveMissingAnswerTarget = (steps, missing) => {
  const visible = Array.isArray(steps) ? steps : [];
  const entries = Array.isArray(missing) ? missing : [];

  for (const entry of entries) {
    const sourceId = String(entry?.source_id || "");
    if (!sourceId) continue;
    const step = visible.find((candidate) =>
      (candidate.itemIds || []).some((itemId) => String(itemId) === sourceId) ||
      (candidate.questions || []).some(
        (question) =>
          String(question?.source_id || "") === sourceId ||
          String(question?.id || "") === sourceId
      )
    );
    if (step) {
      return Object.freeze({
        stepId: step.id,
        sourceId,
        label: entry.label || "",
      });
    }
  }
  return null;
};

export const resolveCareLocationTarget = (steps) => {
  const step = (Array.isArray(steps) ? steps : []).find((candidate) =>
    (candidate.questions || [candidate.question]).some(
      (question) => question?.question_type === "state_routing",
    ),
  );
  return step ? Object.freeze({ stepId: step.id }) : null;
};

/**
 * Patient-facing text for a missing required answer.
 *
 * Deliberately ignores `source_id`: a UUID must never reach the patient. The
 * label is used only for a screen-reader/diagnostic association, not to
 * re-state the question the patient is already looking at.
 */
export const MISSING_ANSWER_MESSAGE = "Please answer this question to continue.";

/** Structured diagnostics for logging. Contains no answers and no PHI. */
export const missingAnswerDiagnostics = (missing) => ({
  code: "required_answers_missing",
  missing_source_ids: (Array.isArray(missing) ? missing : [])
    .map((entry) => String(entry?.source_id || ""))
    .filter(Boolean),
});
